import { Routes } from '@angular/router';
import { authGuard } from './guards/auth.guard';
import { userResolver } from './resolvers/user.resolver';
import { institutionGuard } from './guards/institution.guard';
import { roleGuard } from './guards/role.guard';
import { institutionResolver } from './resolvers/institution.resolver';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./pages/public/login/login.component')
      .then(m => m.LoginComponent)
  },
  {
    path: 'invitation/:token/login',
    loadComponent: () => import('./pages/public/invitation-login/invitation-login.component')
      .then(m => m.InvitationLoginComponent)
  },
  {    
    path: 'invitation/:token/registration',
    loadComponent: () => import('./pages/public/invitation-registration/invitation-registration.component')
      .then(m => m.InvitationRegistrationComponent)
  },
  {
    path: 'invitation/:token/connect',
    loadComponent: () => import('./pages/public/invitation-connect/invitation-connect.component')
      .then(m => m.InvitationConnectComponent)
  },
  {
    path: 'two-factor-verify',
    loadComponent: () => import('./pages/public/two-factor-verify/two-factor-verify.component')
      .then(m => m.TwoFactorVerifyComponent)
  },
  {
    path: 'change-password/request',
    loadComponent: () => import('./pages/public/change-password-request/change-password-request.component')
      .then(m => m.ChangePasswordRequestComponent)
  },
  {
    path: 'change-password/token/:token',
    loadComponent: () => import('./pages/public/change-password/change-password.component')
      .then(m => m.ChangePasswordComponent)
  },
  {
    path: 'institution-select',
    loadComponent: () => import('./pages/public/institution-select/institution-select.component')
      .then(m => m.InstitutionSelectComponent),
    canActivate: [authGuard]
  },
  {
    path: 'institution',
    loadComponent: () => import('./pages/institution/institution.component')
      .then(m => m.InstitutionComponent),
    resolve: { 
      user: userResolver,
      institution: institutionResolver,
    },
    children: [
      {
        path: 'schedule',
        loadComponent: () => import('./pages/institution/schedule/schedule.component')
          .then(m => m.ScheduleComponent),
        canActivate: [authGuard, institutionGuard],
      },
      {
        path: 'patient/list',
        loadComponent: () => import('./pages/institution/patient-list/patient-list.component')
          .then(m => m.PatientListComponent),
        canActivate: [authGuard, institutionGuard],
      },
      {
        path: 'patient/new',
        loadComponent: () => import('./pages/institution/patient-details/patient-details.component')
          .then(m => m.PatientDetailsComponent),
        canActivate: [authGuard, institutionGuard],
      },
      {
        path: 'test/:testId/patient/list',
        loadComponent: () => import('./pages/institution/patient-test-list/patient-test-list.component')
          .then(m => m.PatientTestListComponent),
        canActivate: [authGuard, institutionGuard],
      },
      {
        path: 'patient/:patientId/details',
        loadComponent: () => import('./pages/institution/patient-details/patient-details.component')
          .then(m => m.PatientDetailsComponent),
        canActivate: [authGuard, institutionGuard],
      },
      {
        path: 'patient/:patientId/visit',
        loadComponent: () => import('./pages/institution/patient-visit/patient-visit.component')
          .then(m => m.PatientVisitComponent),
        canActivate: [authGuard, institutionGuard],
      },
      {
        path: 'patient/:patientId/visit/:visitId',
        loadComponent: () => import('./pages/institution/patient-visit/patient-visit.component')
          .then(m => m.PatientVisitComponent),
        canActivate: [authGuard, institutionGuard],
      },
      {
        path: 'settings',
        loadComponent: () => import('./pages/institution/settings/settings.component')
          .then(m => m.SettingsComponent),
        canActivate: [authGuard, institutionGuard],
        children: [
          {
            path: 'promotion/list',
            loadComponent: () => import('./pages/institution/settings/promotion-list/promotion-list.component')
              .then(m => m.PromotionListComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'promotion/new',
            loadComponent: () => import('./pages/institution/settings/promotion-details/promotion-details.component')
              .then(m => m.PromotionDetailsComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'promotion/:promotionId/details',
            loadComponent: () => import('./pages/institution/settings/promotion-details/promotion-details.component')
              .then(m => m.PromotionDetailsComponent),
            canActivate: [authGuard, institutionGuard],
          },   
          {
            path: 'doctor/list',
            loadComponent: () => import('./pages/institution/settings/doctor-list/doctor-list.component')
              .then(m => m.DoctorListComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'doctor/new',
            loadComponent: () => import('./pages/institution/settings/doctor-details/doctor-details.component')
              .then(m => m.DoctorDetailsComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'doctor/:doctorId/details',
            loadComponent: () => import('./pages/institution/settings/doctor-details/doctor-details.component')
              .then(m => m.DoctorDetailsComponent),
            canActivate: [authGuard, institutionGuard],
          },    
          {
            path: 'user/list',
            loadComponent: () => import('./pages/institution/settings/user-list/user-list.component')
              .then(m => m.UserListComponent),
            canActivate: [authGuard, institutionGuard, roleGuard],
            data: {
              roles: ['Global Super Administrator', 'Institution Owner']
            }
          },
          {
            path: 'user/new', 
            loadComponent: () => import('./pages/institution/settings/user-details/user-details.component')
              .then(m => m.UserDetailsComponent),
            canActivate: [authGuard, institutionGuard, roleGuard],
            data: {
              roles: ['Global Super Administrator', 'Institution Owner']
            }
          },
          {
            path: 'user/:userId/details',
            loadComponent: () => import('./pages/institution/settings/user-details/user-details.component')
              .then(m => m.UserDetailsComponent),
            canActivate: [authGuard, institutionGuard, roleGuard],
            data: {
              roles: ['Global Super Administrator', 'Institution Owner']
            }
          },   
          {
            path: 'service/list',
            loadComponent: () => import('./pages/institution/settings/institution-service-list/institution-service-list.component')
              .then(m => m.InstitutionServiceListComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'template/list',
            loadComponent: () => import('./pages/institution/settings/institution-template-list/institution-template-list.component')
              .then(m => m.InstitutionTemplateListComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'template/new',
            loadComponent: () => import('./pages/institution/settings/institution-template-details/institution-template-details.component')
              .then(m => m.InstitutionTemplateDetailsComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'template/:institutionTemplateId/details',
            loadComponent: () => import('./pages/institution/settings/institution-template-details/institution-template-details.component')
              .then(m => m.InstitutionTemplateDetailsComponent),
            canActivate: [authGuard, institutionGuard],
          },
          {
            path: 'details',
            loadComponent: () => import('./pages/institution/settings/institution-details/institution-details.component')
              .then(m => m.InstitutionDetailsComponent),
            canActivate: [authGuard, institutionGuard],
          },
        ]
      },

      {
        path: 'profile',
        loadComponent: () => import('./pages/profile/profile.component')
          .then(m => m.ProfileComponent),
        canActivate: [authGuard, institutionGuard],
      }
    ]
  },
  {
    path: 'admin',
    loadComponent: () => import('./pages/admin/admin.component')
      .then(m => m.AdminComponent),
    canActivate: [authGuard, roleGuard],
    data: {
      roles: ['Global Super Administrator']
    },
    resolve: {
      user: userResolver,
    },
    children: [
      { path: '', redirectTo: '/admin/institution/list', pathMatch: 'full' },
      {
        path: 'institution/list',
        loadComponent: () => import('./pages/admin/institution-list/institution-list.component')
          .then(m => m.InstitutionListComponent),
        canActivate: [authGuard],
      },
      {
        path: 'institution/new',
        loadComponent: () => import('./pages/admin/institution-details/institution-details.component')
          .then(m => m.AdminInstitutionDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'institution/:institutionId/details',
        loadComponent: () => import('./pages/admin/institution-details/institution-details.component')
          .then(m => m.AdminInstitutionDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'test/list',
        loadComponent: () => import('./pages/admin/test-list/test-list.component')
          .then(m => m.TestListComponent),
        canActivate: [authGuard],
      },
      {
        path: 'test/new',
        loadComponent: () => import('./pages/admin/test-details/test-details.component')
          .then(m => m.TestDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'test/:testId/details',
        loadComponent: () => import('./pages/admin/test-details/test-details.component')
          .then(m => m.TestDetailsComponent),
        canActivate: [authGuard],
      },   
      {
        path: 'institution-patient-test/list',
        loadComponent: () => import('./pages/admin/institution-patient-test-list/institution-patient-test-list.component')
          .then(m => m.InstitutionPatientTestListComponent),
        canActivate: [authGuard],
      }, 
      {
        path: 'institution-patient-test/new',
        loadComponent: () => import('./pages/admin/institution-patient-test-details/institution-patient-test-details.component')
          .then(m => m.InstitutionPatientTestDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'institution-patient-test/:institutionPatientTestId/details',
        loadComponent: () => import('./pages/admin/institution-patient-test-details/institution-patient-test-details.component')
          .then(m => m.InstitutionPatientTestDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'therapy/list',
        loadComponent: () => import('./pages/admin/therapy-list/therapy-list.component')
          .then(m => m.TherapyListComponent),
        canActivate: [authGuard],
      },
      {
        path: 'therapy/new',
        loadComponent: () => import('./pages/admin/therapy-details/therapy-details.component')
          .then(m => m.AdminTherapyDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'therapy/:therapyId/details',
        loadComponent: () => import('./pages/admin/therapy-details/therapy-details.component')
          .then(m => m.AdminTherapyDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'disease/list',
        loadComponent: () => import('./pages/admin/disease-list/disease-list.component')
          .then(m => m.DiseaseListComponent),
        canActivate: [authGuard],
      },
      {
        path: 'disease/new',
        loadComponent: () => import('./pages/admin/disease-details/disease-details.component')
          .then(m => m.AdminDiseaseDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'disease/:diseaseId/details',
        loadComponent: () => import('./pages/admin/disease-details/disease-details.component')
          .then(m => m.AdminDiseaseDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'country/list',
        loadComponent: () => import('./pages/admin/country-list/country-list.component')
          .then(m => m.CountryListComponent),
        canActivate: [authGuard],
      },
      {
        path: 'country/new',
        loadComponent: () => import('./pages/admin/country-details/country-details.component')
          .then(m => m.AdminCountryDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'country/:countryId/details',
        loadComponent: () => import('./pages/admin/country-details/country-details.component')
          .then(m => m.AdminCountryDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'city/list',
        loadComponent: () => import('./pages/admin/city-list/city-list.component')
          .then(m => m.CityListComponent),
        canActivate: [authGuard],
      },
      {
        path: 'city/new',
        loadComponent: () => import('./pages/admin/city-details/city-details.component')
          .then(m => m.AdminCityDetailsComponent),
        canActivate: [authGuard],
      },
      {
        path: 'city/:cityId/details',
        loadComponent: () => import('./pages/admin/city-details/city-details.component')
          .then(m => m.AdminCityDetailsComponent),
        canActivate: [authGuard],
      },

      {
        path: 'profile',
        loadComponent: () => import('./pages/profile/profile.component')
          .then(m => m.ProfileComponent),
        canActivate: [authGuard],
      }
    ]
  },
  { path: '', redirectTo: '/institution/schedule', pathMatch: 'full' }
];
